import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { isAxiosError } from 'axios';
import { ui } from '../ui';
import {
  CoverageException, RosterMember,
  getCoverageExceptions, createCoverageException, endCoverageException, deleteCoverageException,
} from '../api';
import { LoadingSpinner } from '../../../components/common/LoadingSpinner';

// Weekday convention across Workforce.* (RecommendedStaffing/GetWeeklyCoverage): 1=Sun..7=Sat.
const DAY_ABBR = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const WEEKDAYS = [1, 2, 3, 4, 5, 6, 7];

const etTodayIso = () => new Intl.DateTimeFormat('en-CA', { timeZone: 'America/New_York' }).format(new Date());

// Date-only display in UTC-midnight space so the viewer's own timezone can't shift the
// displayed day (mirrors TimeTrackingAdmin's fmtGridDate / TimeTrackingUploads' fmtRangeDate).
const fmtDate = (iso: string) =>
  new Date(`${iso}T00:00:00Z`).toLocaleDateString(undefined, { timeZone: 'UTC', month: 'short', day: 'numeric', year: 'numeric' });

// "16:00" -> "4:00 PM"
const fmtTime = (hhmm: string) => {
  const [h, m] = hhmm.split(':').map(Number);
  const h12 = h % 12 === 0 ? 12 : h % 12;
  return `${h12}:${String(m).padStart(2, '0')} ${h < 12 ? 'AM' : 'PM'}`;
};

// Collapses a set of weekdays into "Sun–Sat" / "Mon–Fri" for any contiguous run, otherwise
// a comma list ("Mon, Wed, Fri") — mirrors how an admin would describe the days out loud.
const formatDays = (weekdays: number[]): string => {
  const sorted = Array.from(new Set(weekdays)).sort((a, b) => a - b);
  const isContiguous = sorted.length >= 2 && sorted.every((w, i) => i === 0 || w === sorted[i - 1] + 1);
  if (isContiguous) return `${DAY_ABBR[sorted[0] - 1]}–${DAY_ABBR[sorted[sorted.length - 1] - 1]}`;
  return sorted.map(w => DAY_ABBR[w - 1]).join(', ');
};

// Surfaces the server's 400 { error } message verbatim; falls back to a generic retry
// prompt for network failures / 500s where there's no such body (mirrors TimeTrackingUploads).
const extractErrorMessage = (err: unknown): string => {
  if (isAxiosError(err)) {
    const data = err.response?.data as { error?: string } | undefined;
    if (data?.error) return data.error;
  }
  return 'Request failed — try again.';
};

// One (agent, label, time range, effective range) block, spanning one or more weekday rows.
// Same-shape rows collapse into a single table row; End/Delete apply to every row in the group.
interface ExceptionGroup {
  key: string;
  agentName: string;
  label: string;
  startTimeET: string;
  endTimeET: string;
  effectiveFrom: string;
  effectiveTo: string | null;
  createdBy: string;
  createdAtUtc: string;
  entries: { weekday: number; id: number }[];
}

const groupKey = (ex: CoverageException) =>
  [ex.AgentName, ex.Label, ex.StartTimeET, ex.EndTimeET, ex.EffectiveFrom, ex.EffectiveTo ?? ''].join(' ');

const groupExceptions = (exceptions: CoverageException[]): ExceptionGroup[] => {
  const map = new Map<string, ExceptionGroup>();
  for (const ex of exceptions) {
    const key = groupKey(ex);
    let g = map.get(key);
    if (!g) {
      g = {
        key, agentName: ex.AgentName, label: ex.Label, startTimeET: ex.StartTimeET, endTimeET: ex.EndTimeET,
        effectiveFrom: ex.EffectiveFrom, effectiveTo: ex.EffectiveTo, createdBy: ex.CreatedBy, createdAtUtc: ex.CreatedAtUtc,
        entries: [],
      };
      map.set(key, g);
    }
    g.entries.push({ weekday: ex.Weekday, id: ex.ExceptionID });
  }
  const list = Array.from(map.values());
  list.forEach(g => g.entries.sort((a, b) => a.weekday - b.weekday));
  list.sort((a, b) => a.agentName.localeCompare(b.agentName) || a.startTimeET.localeCompare(b.startTimeET));
  return list;
};

interface FormState {
  agentName: string;
  weekdays: number[];
  startTimeET: string;
  endTimeET: string;
  label: string;
  effectiveFrom: string;
  effectiveTo: string;
}

const emptyForm = (): FormState => ({
  agentName: '', weekdays: [], startTimeET: '', endTimeET: '', label: '', effectiveFrom: etTodayIso(), effectiveTo: '',
});

// Client-side pre-checks only — the server re-validates independently (roster membership,
// overlap against existing exceptions) where there's no way to check without a round trip.
const validateForm = (f: FormState): string | null => {
  if (!f.agentName) return 'Select an agent.';
  if (f.weekdays.length === 0) return 'Select at least one weekday.';
  if (!f.startTimeET || !f.endTimeET) return 'Start and end time are required.';
  if (f.endTimeET === f.startTimeET) return 'End time must be different from start time.';
  if (!f.label.trim()) return 'Label is required.';
  if (!f.effectiveFrom) return 'Effective-from date is required.';
  if (f.effectiveTo && f.effectiveTo < f.effectiveFrom) return 'Effective-to must be on or after effective-from.';
  return null;
};

interface TimeTrackingExceptionsProps {
  // Answering-role roster members, already loaded by TimeTrackingAdmin.
  roster: RosterMember[];
  // Called after any create/end/delete so the parent can refresh the coverage grid
  // (exceptions change ScheduledAgents counts).
  onChanged: () => void;
}

export const TimeTrackingExceptions: React.FC<TimeTrackingExceptionsProps> = ({ roster, onChanged }) => {
  const [exceptions, setExceptions] = useState<CoverageException[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [showEnded, setShowEnded] = useState(false);

  const [form, setForm] = useState<FormState>(emptyForm());
  const [formError, setFormError] = useState<string | null>(null);
  const [formSuccess, setFormSuccess] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const [endingKey, setEndingKey] = useState<string | null>(null);
  const [endDate, setEndDate] = useState(etTodayIso());
  const [busyKey, setBusyKey] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  const refresh = useCallback(() => {
    setLoading(true);
    setLoadError(null);
    return getCoverageExceptions()
      .then(setExceptions)
      .catch(err => setLoadError(extractErrorMessage(err)))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const answeringAgents = useMemo(
    () => roster.filter(r => r.Role === 'Answering' && r.IsActive),
    [roster],
  );

  const today = etTodayIso();
  const groups = useMemo(() => groupExceptions(exceptions), [exceptions]);
  const activeGroups = groups.filter(g => !(g.effectiveTo && g.effectiveTo < today));
  const endedGroups = groups.filter(g => g.effectiveTo && g.effectiveTo < today);

  const toggleWeekday = (d: number) =>
    setForm(prev => ({
      ...prev,
      weekdays: prev.weekdays.includes(d) ? prev.weekdays.filter(w => w !== d) : [...prev.weekdays, d].sort((a, b) => a - b),
    }));

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const err = validateForm(form);
    if (err) { setFormError(err); setFormSuccess(null); return; }
    setSaving(true);
    setFormError(null);
    setFormSuccess(null);
    try {
      const created = await createCoverageException({
        AgentName: form.agentName,
        Weekdays: form.weekdays,
        StartTimeET: form.startTimeET,
        EndTimeET: form.endTimeET,
        Label: form.label.trim(),
        EffectiveFrom: form.effectiveFrom,
        EffectiveTo: form.effectiveTo || undefined,
      });
      const count = Array.isArray(created) ? created.length : form.weekdays.length;
      setFormSuccess(`Added ${count} exception row${count === 1 ? '' : 's'}.`);
      setForm(emptyForm());
      await refresh();
      onChanged();
    } catch (err) {
      setFormError(extractErrorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  const startEnding = (group: ExceptionGroup) => {
    setActionError(null);
    setEndingKey(group.key);
    setEndDate(etTodayIso());
  };
  const cancelEnding = () => setEndingKey(null);

  const confirmEnd = async (group: ExceptionGroup) => {
    setActionError(null);
    setBusyKey(group.key);
    try {
      await Promise.all(group.entries.map(en => endCoverageException(en.id, endDate)));
      setEndingKey(null);
      await refresh();
      onChanged();
    } catch (err) {
      setActionError(extractErrorMessage(err));
    } finally {
      setBusyKey(null);
    }
  };

  const handleDelete = async (group: ExceptionGroup) => {
    const days = formatDays(group.entries.map(en => en.weekday));
    const confirmed = window.confirm(
      `Delete ${group.entries.length} off-call exception row${group.entries.length === 1 ? '' : 's'} for ` +
      `${group.agentName} — ${group.label} (${days}, ${fmtTime(group.startTimeET)}–${fmtTime(group.endTimeET)} ET)? ` +
      'This cannot be undone.',
    );
    if (!confirmed) return;
    setActionError(null);
    setBusyKey(group.key);
    try {
      await Promise.all(group.entries.map(en => deleteCoverageException(en.id)));
      await refresh();
      onChanged();
    } catch (err) {
      setActionError(extractErrorMessage(err));
    } finally {
      setBusyKey(null);
    }
  };

  const renderGroupRow = (group: ExceptionGroup, ended: boolean) => {
    const isEndingThis = endingKey === group.key;
    const isBusy = busyKey === group.key;
    return (
      <React.Fragment key={group.key}>
        <tr className={ended ? 'text-muted opacity-70' : 'hover:bg-surface-hover'}>
          <td className={ui.td}>{group.agentName}</td>
          <td className={ui.td}>{group.label}</td>
          <td className={`${ui.td} whitespace-nowrap`}>{formatDays(group.entries.map(en => en.weekday))}</td>
          <td className={`${ui.td} whitespace-nowrap`}>{fmtTime(group.startTimeET)}–{fmtTime(group.endTimeET)} ET</td>
          <td className={`${ui.td} whitespace-nowrap`}>
            {fmtDate(group.effectiveFrom)} – {group.effectiveTo ? fmtDate(group.effectiveTo) : 'open-ended'}
          </td>
          <td className={ui.td}>{group.createdBy}</td>
          <td className={`${ui.td} text-right`}>
            <div className="flex justify-end gap-2">
              {!ended && (
                <button type="button" className={ui.btnSecondary} onClick={() => startEnding(group)} disabled={isBusy}>
                  End…
                </button>
              )}
              <button type="button" className={ui.btnDanger} onClick={() => handleDelete(group)} disabled={isBusy}>
                {isBusy ? 'Working…' : 'Delete'}
              </button>
            </div>
          </td>
        </tr>
        {isEndingThis && (
          <tr>
            <td colSpan={7} className={`${ui.td} bg-surface`}>
              <form
                className="flex flex-wrap items-end gap-3"
                onSubmit={e => { e.preventDefault(); confirmEnd(group); }}
              >
                <div>
                  <label className={ui.label} htmlFor={`end-date-${group.key}`}>
                    End {group.agentName} — {group.label} effective on
                  </label>
                  <input
                    id={`end-date-${group.key}`}
                    type="date"
                    className={ui.control}
                    value={endDate}
                    onChange={e => setEndDate(e.target.value)}
                  />
                </div>
                <button type="submit" className={ui.btnPrimary} disabled={busyKey === group.key || !endDate}>
                  {busyKey === group.key ? 'Ending…' : 'Confirm'}
                </button>
                <button type="button" className={ui.btnSecondary} onClick={cancelEnding} disabled={busyKey === group.key}>
                  Cancel
                </button>
              </form>
            </td>
          </tr>
        )}
      </React.Fragment>
    );
  };

  return (
    <div className="space-y-4">
      <p className="text-sm text-muted">
        Recurring off-call blocks for Answering-role agents (e.g. a special project) — the agent is
        excluded from the coverage grid's Scheduled count for that weekday/time window, every week the
        block is in effect. Removing a block ends it as of a chosen date (audit trail preserved);
        Delete is a hard remove for mistake-correction only.
      </p>

      {/* ── Add form ─────────────────────────────────────────── */}
      <form className="rounded-md border border-border-default bg-surface p-4" onSubmit={handleSubmit}>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          <div>
            <label className={ui.label} htmlFor="exception-agent">Agent</label>
            <select
              id="exception-agent"
              className={ui.control}
              value={form.agentName}
              onChange={e => setForm(prev => ({ ...prev, agentName: e.target.value }))}
              disabled={saving}
            >
              <option value="">— select agent —</option>
              {answeringAgents.map(r => (
                <option key={r.RosterID} value={r.CanonicalName}>{r.CanonicalName}</option>
              ))}
            </select>
          </div>
          <div>
            <label className={ui.label} htmlFor="exception-label">Label</label>
            <input
              id="exception-label"
              type="text"
              className={ui.control}
              placeholder="e.g. Special Project"
              value={form.label}
              onChange={e => setForm(prev => ({ ...prev, label: e.target.value }))}
              disabled={saving}
            />
          </div>
          <fieldset className="sm:col-span-2 lg:col-span-1">
            <legend className={ui.label}>Weekdays</legend>
            <div className="flex flex-wrap gap-3">
              {WEEKDAYS.map(d => (
                <label key={d} className="flex items-center gap-1 text-sm text-secondary" htmlFor={`exception-weekday-${d}`}>
                  <input
                    id={`exception-weekday-${d}`}
                    type="checkbox"
                    className={ui.checkbox}
                    checked={form.weekdays.includes(d)}
                    onChange={() => toggleWeekday(d)}
                    disabled={saving}
                  />
                  {DAY_ABBR[d - 1]}
                </label>
              ))}
            </div>
          </fieldset>
          <div>
            <label className={ui.label} htmlFor="exception-start">Start (ET)</label>
            <input
              id="exception-start"
              type="time"
              className={ui.control}
              value={form.startTimeET}
              onChange={e => setForm(prev => ({ ...prev, startTimeET: e.target.value }))}
              disabled={saving}
            />
          </div>
          <div>
            <label className={ui.label} htmlFor="exception-end">End (ET)</label>
            <input
              id="exception-end"
              type="time"
              className={ui.control}
              value={form.endTimeET}
              onChange={e => setForm(prev => ({ ...prev, endTimeET: e.target.value }))}
              disabled={saving}
            />
          </div>
          <div>
            <label className={ui.label} htmlFor="exception-effective-from">Effective from</label>
            <input
              id="exception-effective-from"
              type="date"
              className={ui.control}
              value={form.effectiveFrom}
              onChange={e => setForm(prev => ({ ...prev, effectiveFrom: e.target.value }))}
              disabled={saving}
            />
          </div>
          <div>
            <label className={ui.label} htmlFor="exception-effective-to">Effective to (optional)</label>
            <input
              id="exception-effective-to"
              type="date"
              className={ui.control}
              value={form.effectiveTo}
              min={form.effectiveFrom || undefined}
              onChange={e => setForm(prev => ({ ...prev, effectiveTo: e.target.value }))}
              disabled={saving}
            />
          </div>
        </div>

        <div className="mt-4 flex items-center gap-3">
          <button type="submit" className={ui.btnPrimary} disabled={saving}>
            {saving ? 'Adding…' : 'Add Exception'}
          </button>
          <div aria-live="polite" role="status" className="text-sm">
            {formError && <p className="text-red-600">{formError}</p>}
            {!formError && formSuccess && <p className="text-green-600">{formSuccess}</p>}
          </div>
        </div>
      </form>

      {/* ── Table ────────────────────────────────────────────── */}
      {actionError && (
        <div role="alert" className="rounded-md border border-red-300 bg-red-50 px-4 py-3 text-sm text-red-700 dark:border-red-800 dark:bg-red-950 dark:text-red-300">
          {actionError}
        </div>
      )}

      {loading && <LoadingSpinner size="sm" message="Loading off-call exceptions…" />}
      {!loading && loadError && (
        <div role="alert" className="rounded-md border border-red-300 bg-red-50 px-4 py-3 text-sm text-red-700 dark:border-red-800 dark:bg-red-950 dark:text-red-300">
          {loadError}
        </div>
      )}
      {!loading && !loadError && (
        <>
          <div className={ui.tableWrap}>
            <table className={ui.table}>
              <thead className={ui.thead}>
                <tr>
                  <th className={ui.th}>Agent</th>
                  <th className={ui.th}>Label</th>
                  <th className={ui.th}>Days</th>
                  <th className={ui.th}>Time (ET)</th>
                  <th className={ui.th}>Effective</th>
                  <th className={ui.th}>Created By</th>
                  <th className={`${ui.th} text-right`}>Actions</th>
                </tr>
              </thead>
              <tbody className={ui.tbody}>
                {activeGroups.map(g => renderGroupRow(g, false))}
                {activeGroups.length === 0 && (
                  <tr>
                    <td colSpan={7} className="px-4 py-8 text-center text-sm text-muted">
                      No off-call exceptions configured yet — use the form above to add one.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {endedGroups.length > 0 && (
            <div>
              <button type="button" className={ui.btnSecondary} onClick={() => setShowEnded(s => !s)}>
                {showEnded ? 'Hide' : 'Show'} ended exceptions ({endedGroups.length})
              </button>
              {showEnded && (
                <div className={`${ui.tableWrap} mt-2`}>
                  <table className={ui.table}>
                    <thead className={ui.thead}>
                      <tr>
                        <th className={ui.th}>Agent</th>
                        <th className={ui.th}>Label</th>
                        <th className={ui.th}>Days</th>
                        <th className={ui.th}>Time (ET)</th>
                        <th className={ui.th}>Effective</th>
                        <th className={ui.th}>Created By</th>
                        <th className={`${ui.th} text-right`}>Actions</th>
                      </tr>
                    </thead>
                    <tbody className={ui.tbody}>
                      {endedGroups.map(g => renderGroupRow(g, true))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
};
